/**
 * RotationHistoryRow — one recorded rotation run: a scannable summary line
 * (when · who · what counts), expandable to the SAME swap schema the live
 * preview uses — one visual language everywhere.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { RotationRunRecord, RotationRunSource } from '@trafficpulse/shared';

import { Badge, type BadgeProps } from '@/components/ui';
import { formatDateTimeGb } from '@/lib/utils';

import RotationSchemaView from '../rotation/RotationSchemaView';

const SOURCE_META: Record<
  RotationRunSource,
  { label: string; tone: BadgeProps['tone'] }
> = {
  manual: { label: 'Manual', tone: 'info' },
  scheduled: { label: 'Scheduler', tone: 'secondary' },
  restore: { label: 'Restore', tone: 'default' },
  cap: { label: 'CAP tracking', tone: 'warning' },
};

const Counts: FC<{ run: RotationRunRecord }> = ({ run }) => {
  const parts = [
    run.eliminatedCount ? `${run.eliminatedCount} eliminated` : null,
    run.addedCount ? `${run.addedCount} in` : null,
    run.requeuedCount ? `${run.requeuedCount} requeued` : null,
    run.blockedCount ? `${run.blockedCount} blocked` : null,
  ].filter(Boolean);
  return (
    <span className="text-[11px] tabular-nums text-muted-foreground">
      {parts.join(' · ')}
    </span>
  );
};

export interface RotationHistoryRowProps {
  run: RotationRunRecord;
  expanded: boolean;
  onToggle: () => void;
}

const RotationHistoryRow: FC<RotationHistoryRowProps> = ({
  run,
  expanded,
  onToggle,
}) => {
  const Chevron = expanded ? ChevronDown : ChevronRight;
  return (
    <li>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="flex w-full items-center gap-2 px-1 py-2 text-left hover:bg-muted/30"
      >
        <Chevron className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
        <span className="text-xs font-semibold tabular-nums">
          {formatDateTimeGb(run.appliedAt)}
        </span>
        <Badge tone={SOURCE_META[run.source].tone} className="text-[9px]">
          {SOURCE_META[run.source].label}
        </Badge>
        {!run.trackerWrites ? (
          <Badge tone="warning" className="text-[9px]">
            Local only
          </Badge>
        ) : null}
        <span className="ml-auto">
          <Counts run={run} />
        </span>
      </button>
      {expanded ? (
        <div className="border-l-2 border-border/60 py-2 pl-6 pr-1">
          <RotationSchemaView diffs={run.diffs} perPool={run.perPool} />
        </div>
      ) : null}
    </li>
  );
};

export default RotationHistoryRow;
